import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface LeaveAnnotationsDialogProps {
  open: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => Promise<void>;
}

/**
 * Prompts when navigation is leaving the label page with unsaved annotation
 * edits. The navigation itself is intercepted by `useLabelNavigationGuard`,
 * which owns the router's single blocker and decides whether this dialog or
 * the auto-annotation one is the right thing to show.
 */
export const LeaveAnnotationsDialog = ({
  open,
  onCancel,
  onDiscard,
  onSave,
}: LeaveAnnotationsDialogProps) => {
  const { t } = useTranslation(["label", "common"]);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
      onDiscard();
    } catch (e) {
      console.error("Failed to save annotations before leaving", e);
      toast.error(t("leaveDialog.saveFailed"));
      onCancel();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !saving) onCancel();
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <div className="flex flex-row items-center gap-2">
            <TriangleAlert className="size-5 text-amber-500" />
            <DialogTitle>{t("leaveDialog.title")}</DialogTitle>
          </div>
          <DialogDescription>{t("leaveDialog.body")}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            {t("common:actions.cancel")}
          </Button>
          <Button variant="destructive" onClick={onDiscard} disabled={saving}>
            {t("leaveDialog.discard")}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner className="size-4" />
                {t("common:actions.saving")}
              </>
            ) : (
              t("leaveDialog.saveAndLeave")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
